/**
 * 学员端接口函数（一函数一端点，路径与 `docs/04-contracts/openapi.yaml` 对齐）
 * -----------------------------------------------------------------------------
 * 只封装**已实现**的服务端端点；新增端点时同步这里。
 */
import type {
  DeliveryMethod,
  FormSchemaView,
  InvoiceTitleType,
  MaterialStatus,
} from '@enroll/shared';
import { apiRequest, newIdempotencyKey } from './client';
import type {
  EnrollOptionView,
  H5AuthorizeUrl,
  H5EntranceView,
  H5Profile,
  H5ProfileBasic,
  InboxPageView,
  InvoiceRecordView,
  KycAdmissionView,
  KycFaceView,
  KycOcrView,
  KycPhotoView,
  KycStepsView,
  KycStepView,
  PaymentInfoView,
  RegistrationBriefView,
  RegistrationDetailView,
  SaveProfileBasicInput,
  SaveProfileBasicResult,
  ShippingAddressInput,
  SupportQrcodeView,
} from './types';

const BASE = '/api/h5';

export function getSession(): Promise<H5Profile> {
  return apiRequest<H5Profile>(`${BASE}/auth/session`);
}

/**
 * 公众号授权地址（引导页「进入报名」）。
 * `entry` 是入口码，服务端会把它写进微信 `state`，授权回来后回带成 `?entry=`。
 */
export function getAuthorizeUrl(entry?: string | null): Promise<H5AuthorizeUrl> {
  const query = entry ? `?entry=${encodeURIComponent(entry)}` : '';
  return apiRequest<H5AuthorizeUrl>(`${BASE}/auth/authorize-url${query}`);
}

export function listEntrances(): Promise<{ items: H5EntranceView[] }> {
  return apiRequest<{ items: H5EntranceView[] }>(`${BASE}/entrances`);
}

/** 可报「报名类型 × 批次」组合（报名办理页从服务端取列表，入口不再携带参数） */
export function listEnrollOptions(): Promise<{ items: EnrollOptionView[] }> {
  return apiRequest<{ items: EnrollOptionView[] }>(`${BASE}/enroll-options`);
}

export function getForm(enrollTypeId: string): Promise<FormSchemaView> {
  return apiRequest<FormSchemaView>(`${BASE}/forms/${encodeURIComponent(enrollTypeId)}`);
}

export function listMyRegistrations(
  page = 1,
  pageSize = 20,
): Promise<{ items: RegistrationBriefView[]; total: number; page: number; pageSize: number }> {
  return apiRequest(`${BASE}/registrations?page=${page}&pageSize=${pageSize}`);
}

export function getRegistration(id: string): Promise<RegistrationDetailView> {
  return apiRequest<RegistrationDetailView>(`${BASE}/registrations/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------- 消息中心 / 缴费指引

export function fetchInbox(page = 1, pageSize = 20, unreadOnly = false): Promise<InboxPageView> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (unreadOnly) params.set('unreadOnly', 'true');
  return apiRequest<InboxPageView>(`${BASE}/inbox?${params.toString()}`);
}

export function markInboxRead(id: string): Promise<unknown> {
  return apiRequest(`${BASE}/inbox/${encodeURIComponent(id)}/read`, { method: 'POST' });
}

/** 未读数 = 未读列表的 total（只取 1 条，拉计数不加载数据） */
export function fetchInboxUnreadCount(): Promise<number> {
  return fetchInbox(1, 1, true).then((page) => page.total);
}

export function fetchPaymentInfo(registrationId: string): Promise<PaymentInfoView> {
  return apiRequest<PaymentInfoView>(`${BASE}/payment/${encodeURIComponent(registrationId)}/info`);
}

// ---------------------------------------------------------------- 发票申请（口径 C-25）

/** `POST /api/h5/registrations/{id}/invoice` 的请求体（openapi `InvoiceApplyRequest`） */
export interface InvoiceApplyInput {
  titleType: InvoiceTitleType;
  title: string;
  /** 纳税人识别号：单位抬头必填（缺失 → 服务端 `INVOICE-001`） */
  taxNo?: string;
  remark?: string;
}

/**
 * 取本单**当前**开票记录；从未申请过时服务端返回 `null`。
 *
 * 往返的是「最新一行」：驳回后再申请会新建一行，因此这里天然拿到最新状态，
 * 前端不需要自己维护「哪条才算当前」。
 */
export function getMyInvoice(registrationId: string): Promise<InvoiceRecordView | null> {
  return apiRequest<InvoiceRecordView | null>(
    `${BASE}/registrations/${encodeURIComponent(registrationId)}/invoice`,
  );
}

/**
 * 提交开票申请（落 `REQUESTED`，**不代表已开票**）。
 *
 * 金额**不在请求体里** —— 服务端按缴费对账基准写入，前端连表达它的能力都没有。
 * 也刻意**不带幂等令牌**：重复提交由服务端 `INVOICE-002`（部分唯一索引兜并发）
 * 收敛为 409，加一个服务端不读的头部只会让人误以为「重放是安全的」。
 */
export function applyInvoice(
  registrationId: string,
  input: InvoiceApplyInput,
): Promise<InvoiceRecordView> {
  return apiRequest<InvoiceRecordView>(
    `${BASE}/registrations/${encodeURIComponent(registrationId)}/invoice`,
    { method: 'POST', body: input },
  );
}

// ---------------------------------------------------------------- 客服码（联系客服）

/**
 * 客服码的区域查询参数 —— 三级**行政区划码**（与表单 `region` 字段同口径）。
 * 缺省（`undefined`）表示该级不限定，服务端按「覆盖到该级全域」理解。
 */
export interface SupportQrcodeRegion {
  provinceCode?: string;
  cityCode?: string;
  districtCode?: string;
}

/**
 * 取客服码（`GET /api/h5/support/qrcode`，**公开只读**端点）。
 *
 * 服务端按报名邮寄地址区域**四级匹配**（区 → 市 → 省 → 全局缺省码），命中即返回。
 *
 * ✅ **2026-10-17 起不传 `region` 也能命中正确区域**：区域由**服务端从会话内**
 * 「最近一张填了收件地址的报名单」推导（`resolveForStudent`）——
 * 学员端既没有「读地址」端点、也不该拿本地缓存伪造区域，所以在页面上**不传参是正常的**。
 * 传了 `region` 则以显式值为准（引导页 / 预览用）。
 *
 * 无任何可用码（缺省码未配置 / 停用 / 图片缺失）→ `SUPPORT-004`（404）。
 * 调用方应把它当**正常业务分支**（含文字兜底路径）处理，不渲染裂图、不弹错误页。
 */
export function fetchSupportQrcode(region?: SupportQrcodeRegion): Promise<SupportQrcodeView> {
  const params = new URLSearchParams();
  if (region?.provinceCode) params.set('provinceCode', region.provinceCode);
  if (region?.cityCode) params.set('cityCode', region.cityCode);
  if (region?.districtCode) params.set('districtCode', region.districtCode);
  const query = params.toString();
  return apiRequest<SupportQrcodeView>(`${BASE}/support/qrcode${query ? `?${query}` : ''}`);
}

// ---------------------------------------------------------------- 手机号短信验证（附件1 方式一）

/**
 * 下发短信验证码。
 *
 * `debugCode` **只在本地/测试**出现（服务端 `SMS_DEBUG_CODE=true` 且非生产）：
 * 一期短信通道是 mock，码不会到达手机，前端拿到它就自动填入并直接校验。
 * 生产环境这个字段恒为 `undefined`，前端不要依赖它做任何判断。
 */
/** 图形验证码（2026-10-27 加，短信防刷）—— `image` 是 PNG 的 data URL，直接塞 `<img src>` */
export interface PhoneCaptcha {
  captchaId: string;
  image: string;
  expiresInSeconds: number;
  /** 仅本地 / 测试（`SMS_DEBUG_CODE=true`）：服务端把答案回显出来供自动填入 */
  debugCaptchaCode?: string;
}

export function fetchPhoneCaptcha(): Promise<PhoneCaptcha> {
  return apiRequest(`${BASE}/phone/captcha`);
}

export function sendPhoneCode(
  phone: string,
  captcha: { captchaId: string; captchaCode: string },
): Promise<{ cooldownSeconds: number; debugCode?: string }> {
  return apiRequest(`${BASE}/phone/codes`, {
    method: 'POST',
    body: { phone, captchaId: captcha.captchaId, captchaCode: captcha.captchaCode },
  });
}

export function verifyPhoneCode(phone: string, code: string): Promise<{ verified: boolean }> {
  return apiRequest(`${BASE}/phone/codes/verify`, { method: 'POST', body: { phone, code } });
}

export function changeMyPhone(phone: string, code: string): Promise<{ phoneMasked: string }> {
  return apiRequest(`${BASE}/profile/phone`, { method: 'PUT', body: { phone, code } });
}

/**
 * 本人账号资料（姓名 + 手机号**明文**）。
 * 只给「填写页回显」与「报名表单预填」用；纯展示请用 session 里的 `phoneMasked`。
 */
export function getProfileBasic(): Promise<H5ProfileBasic> {
  return apiRequest<H5ProfileBasic>(`${BASE}/profile/basic`);
}

/**
 * 保存账号资料 —— 强制填写页的唯一写入口。
 * 账号还没有手机号时，`phone` + `code` 必传（服务端会以 `PROFILE-002` 拦）。
 */
export function saveProfileBasic(input: SaveProfileBasicInput): Promise<SaveProfileBasicResult> {
  return apiRequest<SaveProfileBasicResult>(`${BASE}/profile/basic`, {
    method: 'PUT',
    body: input,
  });
}

export function createRegistration(
  input: { enrollTypeId: string; batchId: string },
  idempotencyKey: string = newIdempotencyKey(),
): Promise<RegistrationDetailView> {
  return apiRequest<RegistrationDetailView>(`${BASE}/registrations`, {
    method: 'POST',
    body: input,
    idempotencyKey,
  });
}

export function saveDraft(
  id: string,
  formData: Record<string, unknown>,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<RegistrationDetailView> {
  return apiRequest<RegistrationDetailView>(`${BASE}/registrations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { formData },
    idempotencyKey,
  });
}

export function submitRegistration(
  id: string,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<RegistrationDetailView> {
  return apiRequest<RegistrationDetailView>(
    `${BASE}/registrations/${encodeURIComponent(id)}/submit`,
    { method: 'POST', idempotencyKey },
  );
}

export function setDeliveryMethod(
  id: string,
  deliveryMethod: DeliveryMethod,
): Promise<RegistrationDetailView> {
  return apiRequest<RegistrationDetailView>(
    `${BASE}/registrations/${encodeURIComponent(id)}/delivery-method`,
    { method: 'PUT', body: { deliveryMethod } },
  );
}

export function saveAddress(
  id: string,
  address: ShippingAddressInput,
): Promise<RegistrationDetailView> {
  return apiRequest<RegistrationDetailView>(
    `${BASE}/registrations/${encodeURIComponent(id)}/address`,
    {
      method: 'PUT',
      body: address,
    },
  );
}

/** 提交教材领取申请（待申请领取 → 待发货）。幂等：已申请过会原样返回当前状态 */
export function claimMaterial(id: string): Promise<{
  materialStatus: MaterialStatus;
  materialAppliedAt: string | null;
  duplicated: boolean;
}> {
  return apiRequest(`${BASE}/registrations/${encodeURIComponent(id)}/material-claim`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------- 核验链（提交前）

function imageFormData(
  blob: Blob,
  filename: string,
  extra?: Record<string, string | undefined>,
): FormData {
  const form = new FormData();
  form.append('file', blob, filename);
  // 空值必须跳过：`append(key, undefined)` 会写成字符串 "undefined"，
  // 服务端按 `registrationId` 查归属时会得到一个查不到的 id
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value !== undefined && value !== null) form.append(key, value);
  }
  return form;
}

/**
 * 核验状态回显（唯一读接口）。
 *
 * 进页面就拉一次：刷新 / 换设备后，头像、有效期、比对分数都还能看到 ——
 * 这些东西早就落在服务端了，缺的只是一个出口。
 */
export function kycSteps(registrationId: string): Promise<KycStepsView> {
  return apiRequest<KycStepsView>(`${BASE}/kyc/${encodeURIComponent(registrationId)}/steps`);
}

export function kycOcr(registrationId: string, image: Blob): Promise<KycOcrView> {
  return apiRequest<KycOcrView>(`${BASE}/kyc/${encodeURIComponent(registrationId)}/ocr`, {
    method: 'POST',
    formData: imageFormData(image, 'id-card.jpg'),
  });
}

/**
 * 国徽面裁剪留档。
 *
 * 与人像面走同一个供应商接口，但**不是核验步骤**：返回的只是那张扶正裁剪后的
 * 干净图（不带任何识别字段、没有"通不通过"）。所以它的出参没有 `result`/`score`。
 */
export function kycIdCardBack(registrationId: string, image: Blob): Promise<{ imageUrl: string }> {
  return apiRequest<{ imageUrl: string }>(
    `${BASE}/kyc/${encodeURIComponent(registrationId)}/id-card-back`,
    {
      method: 'POST',
      formData: imageFormData(image, 'id-card-back.jpg'),
    },
  );
}

export function kycIdCheck(registrationId: string, idCardNo: string): Promise<KycStepView> {
  return apiRequest<KycStepView>(`${BASE}/kyc/${encodeURIComponent(registrationId)}/id-check`, {
    method: 'POST',
    body: { idCardNo },
  });
}

/**
 * ④ 头像比对。
 *
 * **不传图**：比对的两侧已在库里（① 的身份证头像切图、③ 的证件照）。
 * 让前端再传一张，等于开了「换一张图去比对」的口子。
 */
export function kycFace(registrationId: string): Promise<KycFaceView> {
  return apiRequest<KycFaceView>(`${BASE}/kyc/${encodeURIComponent(registrationId)}/face`, {
    method: 'POST',
  });
}

/**
 * 证件照。`captureSource` **恒为 `CAMERA`** —— 服务端对非 `CAMERA` 的来源返回
 * `KYC-301`（这是防绕过的第二道门）。
 *
 * ⚠️ 这个字段记的是**入口**而不是真实来源：2026-10-26 起证件照允许从相册选图，
 * 而选图时前端拿不到「拍照还是选图」的信息（微信的合并菜单不回调选了什么），
 * 因此选图也记 `CAMERA`。真实度由 ④ 头像比对把关。审计上要区分两者，
 * 需要给契约加 `PICKER` 枚举值，属契约变更，未做（见 `tasks/T-08`）。
 */
export function kycPhoto(registrationId: string, image: Blob): Promise<KycPhotoView> {
  return apiRequest<KycPhotoView>(`${BASE}/kyc/${encodeURIComponent(registrationId)}/photo`, {
    method: 'POST',
    formData: imageFormData(image, 'id-photo.jpg', { captureSource: 'CAMERA' }),
  });
}

export function kycAdmissionCheck(registrationId: string): Promise<KycAdmissionView> {
  return apiRequest<KycAdmissionView>(
    `${BASE}/kyc/${encodeURIComponent(registrationId)}/admission-check`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------- 表单附件 / 手写签名（C-23）

/** 学员侧允许的资产用途（服务端白名单同源；KYC 影像走核验链端点，不经过这里） */
export type H5UploadPurpose = 'FORM_ATTACHMENT' | 'FORM_SIGNATURE';

/** `POST /api/h5/uploads` 出参（openapi 同名 schema） */
export interface H5UploadResult {
  assetId: string;
  purpose: string;
  mimeType: string;
  sizeBytes: number;
}

/**
 * 上传表单附件 / 手写签名图片，返回 `assetId`。
 * `upload` / `signature` 字段的表单答案存的就是这个 assetId（字符串）。
 */
export function uploadFormAsset(
  file: Blob,
  filename: string,
  purpose: H5UploadPurpose,
  /**
   * 归属的报名单（可选）。
   *
   * 带上它，服务端才能在**提交时**删掉「传过但最终没被引用」的旧附件（换图 / 重签
   * 都会留下一个孤儿）。不带也能传 —— 入库时 owner 为空，只是永远不会被清理。
   */
  registrationId?: string,
): Promise<H5UploadResult> {
  return apiRequest<H5UploadResult>(`${BASE}/uploads`, {
    method: 'POST',
    formData: imageFormData(file, filename, { purpose, registrationId }),
  });
}
